export const LOGIN_PATH = "/login";
export const OIDC_CALLBACK_PATH = "/c/oidc/callback";

export type OidcLoginPathOptions = {
  defaultRedirectPath: string;
  redirectPath?: string | null;
  loggedOut?: boolean;
  oidcFailed?: boolean;
};

export type OidcLoginPageQueryState = {
  redirectPath: string;
  loggedOut: boolean;
  oidcFailed: boolean;
  shouldSkipAutoBootstrap: boolean;
};

function readSearchParams(search: string) {
  const normalized = search.startsWith("?") ? search.slice(1) : search;
  return new URLSearchParams(normalized);
}

export function sanitizeOidcRedirectPath(
  rawRedirectPath: string | null | undefined,
  fallbackRedirectPath: string,
) {
  const safeFallbackPath = fallbackRedirectPath.startsWith("/") && !fallbackRedirectPath.startsWith("//")
    ? fallbackRedirectPath
    : "/";
  if (!rawRedirectPath) {
    return safeFallbackPath;
  }
  if (!rawRedirectPath.startsWith("/") || rawRedirectPath.startsWith("//")) {
    return safeFallbackPath;
  }
  return rawRedirectPath;
}

export function buildOidcLoginPath({
  defaultRedirectPath,
  redirectPath,
  loggedOut = false,
  oidcFailed = false,
}: OidcLoginPathOptions) {
  const safeDefaultRedirectPath = sanitizeOidcRedirectPath(defaultRedirectPath, "/");
  const safeRedirectPath = sanitizeOidcRedirectPath(redirectPath, safeDefaultRedirectPath);
  const query = new URLSearchParams();
  if (safeRedirectPath !== safeDefaultRedirectPath) {
    query.set("redirect", safeRedirectPath);
  }
  if (loggedOut) {
    query.set("logged_out", "1");
  }
  if (oidcFailed) {
    query.set("oidc_failed", "1");
  }
  const suffix = query.toString();
  return suffix ? `${LOGIN_PATH}?${suffix}` : LOGIN_PATH;
}

export function readOidcLoginPageQueryState(
  search: string,
  defaultRedirectPath: string,
): OidcLoginPageQueryState {
  const query = readSearchParams(search);
  const loggedOut = query.get("logged_out") === "1";
  const oidcFailed = query.get("oidc_failed") === "1";
  return {
    redirectPath: sanitizeOidcRedirectPath(query.get("redirect"), defaultRedirectPath),
    loggedOut,
    oidcFailed,
    shouldSkipAutoBootstrap: loggedOut || oidcFailed,
  };
}
